import { useNavigate, useSearchParams } from 'react-router-dom'

import { useArticleDetailApi } from '@/common/api/modules/articles'
import {
  useMediaActionDetailApi,
  useUpdateMediaActionMutation
} from '@/common/api/modules/mediaAction'
import { articleDetailPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'

import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import ContentEditLoading from '@/components/view/molecules/loading/ContentEditLoading'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import ArticleCreateForm from '@/components/container/form/modules/media/article/ArticleCreateForm'
import {
  ArticleCreateSchemaType,
  convertProposalDetailToCreateSchema
} from '@/components/container/form/modules/media/article/articleCreateSchema'
import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'
import GscKeywordAnalysisModalProvider from '@/components/container/modal/article/GscKeywordAnalysisModalProvider'
import KeywordHistoryResultModalProvider from '@/components/container/modal/article/KeywordHistoryResultModalProvider'
import KeywordSuggestionModalProvider from '@/components/container/modal/article/KeywordSuggestionModalProvider'
import MediaActionSourceBanner from '@/components/container/modules/media/action/MediaActionSourceBanner'

// 記事作成（新規）：キーワード選定 → AI本文生成 → 保存
const ArticleEditPage = () => {
  const siteId = useSiteId()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const actionId = searchParams.get('action_id')
  const copiedId = searchParams.get('copiedId')

  const { data: actionDetail, isLoading: actionLoading } = useMediaActionDetailApi(
    actionId ? { site_id: siteId, action_id: Number(actionId) } : null
  )
  const { data: copySource, isLoading: copySourceLoading } = useArticleDetailApi(
    copiedId ? { site_id: siteId, article_proposal_id: Number(copiedId) } : null
  )
  const { trigger: updateAction } = useUpdateMediaActionMutation()

  const handleCreated = (articleProposalId: number) => {
    if (actionId) {
      updateAction({ site_id: siteId, action_id: Number(actionId), status: 'done' }).catch(
        () => undefined
      )
    }
    navigate(articleDetailPath(siteId, articleProposalId))
  }

  if ((actionId && actionLoading) || (copiedId && copySourceLoading)) {
    return (
      <Main type={MainLayoutType.MEDIUM}>
        <PageTitle
          title='AI記事作成'
          badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
        />
        <ContentEditLoading center withSide />
      </Main>
    )
  }

  const defaultValues: Partial<ArticleCreateSchemaType> | undefined = actionDetail
    ? {
        title: actionDetail.action.title,
        keyword: actionDetail.action.target_keywords?.join(', ') ?? ''
      }
    : copySource
      ? convertProposalDetailToCreateSchema(copySource)
      : undefined

  return (
    <KeywordSuggestionModalProvider>
      <GscKeywordAnalysisModalProvider>
        <KeywordHistoryResultModalProvider>
          <Main type={MainLayoutType.MEDIUM}>
            <PageTitle
              title='AI記事作成'
              badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
              description='キーワード選定から原稿チェックまで、AI支援で特集コンテンツを作成します'
            />
            {actionDetail && <MediaActionSourceBanner detail={actionDetail} />}
            <ArticleCreateForm
              siteId={siteId}
              defaultValues={defaultValues}
              onCreated={handleCreated}
              sourceActionId={actionId ? Number(actionId) : null}
            />
          </Main>
        </KeywordHistoryResultModalProvider>
      </GscKeywordAnalysisModalProvider>
    </KeywordSuggestionModalProvider>
  )
}

export default ArticleEditPage
